/**
 * DATA TASK DETAIL PAGE
 *
 * Data configuration form for data pipeline setup.
 * Allows Data team to configure and test data pipelines.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { createClient } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/status-badge';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { MapPin, ArrowLeft } from 'lucide-react';
import { CompleteDataTaskButton } from '@/components/complete-data-task-button';
import type { Task, Organization, Location } from '@/types/database';

interface TaskWithRelations extends Task {
  organization: Pick<Organization, 'id' | 'name' | 'status' | 'logo_url'> | null;
  location: Location | null;
}

function DataTaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [task, setTask] = useState<TaskWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [contactInfo, setContactInfo] = useState<any>(null);

  // Auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || (user.role !== 'DATA_TEAM' && user.role !== 'DATA_ADMIN' && user.role !== 'SYSTEM_ADMIN')) {
    navigate('/unauthorized');
    return null;
  }

  // Data fetching
  useEffect(() => {
    const fetchTask = async () => {
      if (!taskId) {
        navigate('/data-tasks');
        return;
      }

      try {
        setLoading(true);
        const supabase = createClient();

        // Fetch task with organization and location details
        const { data: taskData, error: taskError } = await supabase
          .from('tasks')
          .select(`
            *,
            organization:organizations(id, name, status, logo_url),
            location:locations(
              id,
              name,
              description,
              logo_url,
              status,
              mapping_status,
              open_dental_source_schema,
              open_dental_clinic
            )
          `)
          .eq('id', taskId)
          .eq('type', 'DATA_CONFIGURATION')
          .single();

        if (taskError || !taskData) {
          console.error('Error fetching task:', taskError);
          navigate('/data-tasks');
          return;
        }

        setTask(taskData as TaskWithRelations);

        // Fetch onboarding_queue data separately using organization ID
        if (taskData.organization_id) {
          const { data: queueData } = await supabase
            .from('onboarding_queue')
            .select('primary_contact_name, primary_contact_email, phone_number')
            .eq('id', taskData.organization_id)
            .maybeSingle();

          if (queueData) {
            setContactInfo(queueData);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTask();
  }, [taskId, navigate]);

  // Render states
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading task...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">Error loading task</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!task) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back Button */}
      <button
        onClick={() => navigate('/data-tasks')}
        className="flex items-center gap-2 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm font-medium">Back to Data Tasks</span>
      </button>

      {/* Header - Focus on Location */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          {/* Location Logo */}
          {task.location?.logo_url ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-20 h-20 rounded-full overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800">
                  <img
                    src={task.location.logo_url}
                    alt={`${task.location?.name} logo`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">Location Logo</TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-20 h-20 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0">
                  <MapPin className="w-10 h-10 text-gray-400" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">Location Logo</TooltipContent>
            </Tooltip>
          )}

          {/* Header Content */}
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{task.location?.name}</span>
                </TooltipTrigger>
                <TooltipContent side="top">Location</TooltipContent>
              </Tooltip>
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{task.organization?.name}</span>
                </TooltipTrigger>
                <TooltipContent side="top">Organization</TooltipContent>
              </Tooltip>
            </p>
            <div className="mt-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <StatusBadge status={task.status} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">Status</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {/* Primary Contact Information */}
      {contactInfo && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Primary Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {contactInfo.primary_contact_name && (
                <div>
                  <p className="text-xs text-muted-foreground">Contact Name</p>
                  <p className="font-medium text-sm">{contactInfo.primary_contact_name}</p>
                </div>
              )}
              {contactInfo.primary_contact_email && (
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="font-medium text-sm">{contactInfo.primary_contact_email}</p>
                </div>
              )}
              {contactInfo.phone_number && (
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="font-medium text-sm">{contactInfo.phone_number}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Location & Organization Details - Read-only Information */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Configuration Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* IDs */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Organization ID</p>
              <p className="font-mono text-sm text-gray-700 dark:text-gray-300 break-all">
                {task.organization_id}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Location ID</p>
              <p className="font-mono text-sm text-gray-700 dark:text-gray-300 break-all">
                {task.location_id}
              </p>
            </div>
          </div>

          {/* OpenDental Configuration - prefilled by IT team */}
          <div className="pt-4 border-t space-y-3">
            <h4 className="text-sm font-semibold">OpenDental Configuration</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Source Schema</p>
                <p className="font-mono text-sm bg-gray-50 dark:bg-gray-900 p-2 rounded text-gray-700 dark:text-gray-300">
                  {(task.location as any)?.open_dental_source_schema || 'Not configured'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Clinic Number</p>
                <p className="font-mono text-sm bg-gray-50 dark:bg-gray-900 p-2 rounded text-gray-700 dark:text-gray-300">
                  {(task.location as any)?.open_dental_clinic || 'Not configured'}
                </p>
              </div>
            </div>
          </div>

          {/* Description */}
          {task.location?.description && (
            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground mb-1">Location Description</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">{task.location.description}</p>
            </div>
          )}

          {/* Complete Button */}
          <div className="pt-4 border-t">
            <CompleteDataTaskButton
              task={task}
              location={task.location}
              userId={user.id}
            />
          </div>
        </CardContent>
      </Card>

    </div>
  );
}

export default DataTaskDetailPage;
